// src/components/Home.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import LogoutButton from "./CustomButton";
import ThreeDHover from "./3DHover"; // Add this import
import { TrashIcon } from "@heroicons/react/24/outline"; // Add this import
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { BeatLoader } from "react-spinners";
import { fetchCurrentlyPlaying } from "../utils/fetchCurrentlyPlaying"; // Import the utility function

const Home = () => {
  const [user, setUser] = useState(null);
  const [notes, setNotes] = useState([]); // State to hold user notes
  const [currentlyPlayingTrack, setCurrentlyPlayingTrack] = useState(null); // Local state for currently playing track
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_FLASK_API;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [spotifyToken, setSpotifyToken] = useState(null); // Add this state
  const [csrfToken, setCsrfToken] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${apiUrl}/current_user`, {
          method: "GET",
          credentials: "include",
        });
        const userData = await response.json();
        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        setUser(userData);
        const sortedNotes = userData.notes.sort(
          (a, b) => new Date(b[2]) - new Date(a[2])
        );
        setNotes(sortedNotes);

        // Set Spotify token if available
        if (userData.spotify_token) {
          setSpotifyToken(userData.spotify_token);
        }

        // Fetch CSRF token
        const csrfResponse = await fetch(`${apiUrl}/csrf-token`);
        const csrfData = await csrfResponse.json();
        setCsrfToken(csrfData.csrf_token);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data. Please try again later.");
      }
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    let isMounted = true; // Add mounted check

    const getCurrentlyPlaying = async () => {
      try {
        const trackData = await fetchCurrentlyPlaying();
        if (isMounted && trackData) {
          // Only update state if component is mounted and trackData exists
          setCurrentlyPlayingTrack(trackData);
        }
      } catch (error) {
        console.error("Error fetching currently playing track:", error);
      }
    };

    getCurrentlyPlaying(); // Initial fetch
    const intervalId = setInterval(getCurrentlyPlaying, 5000);

    return () => {
      isMounted = false; // Cleanup
      clearInterval(intervalId);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      const response = await fetch(`${apiUrl}/sign_out`, {
        method: "GET",
        credentials: "include",
      });
      const data = await response.json();
      setUser(null);
      console.log(data.message);
      // Use window.location.href for redirect
      window.location.href = "/";
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleStartNote = async () => {
    try {
      const response = await fetch(`${apiUrl}/create_note`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": csrfToken,
        },
        body: JSON.stringify({
          content: "",
          user_id: user.id,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to create note");
      }
      const newNote = await response.json();
      console.log("New Note Created:", newNote);
      navigate(`/notes/${newNote.note_id}`); // Navigates using the UUID
    } catch (error) {
      console.error("Error creating note:", error);
      setError("Failed to create note. Please try again later.");
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      console.log("Attempting to delete note:", noteId);
      console.log("API URL:", `${apiUrl}/delete_note/${noteId}`);

      const response = await fetch(`${apiUrl}/delete_note/${noteId}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          // Removed CSRF token since endpoint is exempt
        },
      });

      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error details:", errorData);
        throw new Error(
          `Failed to delete note: ${errorData.message || response.statusText}`
        );
      }

      setNotes((prevNotes) => prevNotes.filter(([id]) => id !== noteId));
      setShowDeleteModal(false);
      console.log("Note deleted successfully");
    } catch (error) {
      console.error("Error deleting note:", error);
      setError(`Failed to delete note: ${error.message}`);
    }
  };

  const handleDeleteClick = (noteId, noteContent) => {
    setNoteToDelete({ id: noteId, content: noteContent });
    setShowDeleteModal(true);
  };

  const NoteCard = ({ noteId, noteContent, updated_at, onClick, onDelete }) => {
    return (
      <ThreeDHover
        className="bg-gray-800/50 backdrop-blur-lg rounded-xl p-4 border border-gray-700 shadow-xl 
                  hover:bg-gray-700/50 transition-all duration-300 cursor-pointer"
        onClick={onClick}
        intensity={10}
        scale={1.02}
      >
        <div className="flex justify-between items-start w-full">
          <div
            className="relative z-10 transform-style-3d flex-1 min-w-0"
            style={{ transform: "translateZ(20px)" }}
          >
            <p className="text-gray-200 font-medium truncate mb-2">
              {noteContent || "Empty note"}
            </p>
            <p className="text-gray-400 text-sm">
              {new Date(updated_at).toLocaleString()}
            </p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(noteId, noteContent);
            }}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
            style={{ transform: "translateY(25%)" }}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </ThreeDHover>
    );
  };

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex justify-center items-center h-screen">
        <BeatLoader
          color="#c084fc"
          size={50}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white pb-24">
      <div className="container mx-auto px-4 py-8">
        {/* Username Display */}
        <div className="flex justify-between items-center mb-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 text-transparent bg-clip-text">
              {user.display_name}
            </h1>
          </div>
          <LogoutButton onClick={handleSignOut} />
        </div>

        {/* Currently Playing Section */}
        {currentlyPlayingTrack && (
          <div className="mb-8 bg-gray-800/50 backdrop-blur-lg rounded-2xl p-6 border border-gray-700 shadow-xl">
            <h2 className="text-xl font-semibold mb-4 text-purple-400">
              Currently Playing
            </h2>
            <div className="flex items-center gap-4">
              <img
                src={currentlyPlayingTrack.album_image}
                alt="Album Cover"
                className="w-20 h-20 rounded-lg shadow-lg"
              />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-lg truncate">
                  {currentlyPlayingTrack.name || "No track currently playing"}
                </p>
                <p className="text-gray-400 truncate">
                  {currentlyPlayingTrack.artists.join(", ") || ""}
                </p>
                <p className="text-gray-500 truncate">
                  {currentlyPlayingTrack.album || "Unknown Album"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notes Section */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-4 text-purple-400 flex justify-between items-center">
            Your Notes
            {/* Add Note Button */}
            <button
              onClick={handleStartNote}
              className="flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm lg:text-base whitespace-nowrap"
            >
              <svg
                className="w-4 h-4 lg:w-5 lg:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {notes.map(([noteId, noteContent, updated_at]) => (
              <NoteCard
                key={noteId}
                noteId={noteId}
                noteContent={noteContent}
                updated_at={updated_at}
                onClick={() => navigate(`/notes/${noteId}`)}
                onDelete={handleDeleteNote}
              />
            ))}
          </div>
        </div>
      </div>
      {showDeleteModal && (
        <DeleteConfirmationModal
          onConfirm={() => handleDeleteNote(noteToDelete.id)}
          onCancel={() => setShowDeleteModal(false)}
          noteContent={noteToDelete.content}
        />
      )}
    </div>
  );
};

export default Home;
